/* eslint-disable indent */
import _, { isEmpty, isFunction } from 'lodash';
import debounce from 'lodash/debounce';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { v4 as uuid, validate } from 'uuid';


import QCXRegistrationFormPageTemplate from '../../../../../templates/registration-form-page/QCXRegistrationFormPageTemplate';
import TarifarioMaritimoWizardFinalForm from '../components/TarifarioMaritimoWizardFinalForm';


import {
  selectBackgroundMode,
  selectMode,
  selectStatus,
  selectSubBackgroundMode,
  selectTarifario,
  selectUnidadesSelecionadas,
} from '../../../../../features/tarifario/tarifarioSelectors';

import {
  changeToBackgroundCreateMode,
  resetBackgroundMode,
  preparingAction,
  changeToCreateMode,
  changeToConsultMode,
  changeToUpdateMode,
  resetModel,
  success,
  setModel,
  failure,
  loading,
  setError,
  setResponse,
  resetSubMode,
} from '../../../../../features/tarifario/tarifarioSlice';

import { fetchByIdAsync } from '../../../../../features/tarifario/tarifarioThunks'

import { tarifarioAPI } from '../../../../../features/tarifario/tarifarioAPI';

import {
  isBackgroundCreateMode,
  isConsultMode,
  isCreateMode,
  isPreparingActionStatus,
  isUpdateMode,
} from '../../../../../utils/store/store-utils';



import { normalizeNumeral, unnormalizeNumeral } from '../../../../../utils/general/general-utils';


import {
  fetchAllAsync as fetchViasTransporteAsync,
  selectViasTransporte,
} from '../../../../../features/via-transporte/viaTransporteSlice';


export default function TarifarioMaritimoRegistrationPage({ authInfo = {} }) {

  const { t } = useTranslation();

  const dispatch = useDispatch();
  
  const { id } = useParams();

  const history = useHistory();

  const freteTarifario = useSelector(selectTarifario);
  const unidadesNegocioSelecionadas = useSelector(selectUnidadesSelecionadas);
  
  const [isCreated, setIsCreated] = useState(false);

  const status = useSelector(selectStatus);
  const mode = useSelector(selectMode);
  const backgroundMode = useSelector(selectBackgroundMode);

  const isPreparingAction = useMemo(() => isPreparingActionStatus(status), [status]);
  const isCreate = useMemo(() => isCreateMode(mode), [mode]);
  const isConsult = useMemo(() => isConsultMode(mode), [mode]);
  const isUpdate = useMemo(() => isUpdateMode(mode), [mode]);
  const isBackgroundCreate = useMemo(() => isBackgroundCreateMode(backgroundMode), [backgroundMode]);


  const viasTransporteList = useSelector(selectViasTransporte);

  const viaTransporteMaritima = useMemo(() => {
    if (!viasTransporteList || viasTransporteList.length === 0) {
      return null;
    }

    return viasTransporteList.find((via) => via.description?.toLowerCase() === 'maritima' || via.description?.toLowerCase() === 'maritima');
  }, [ viasTransporteList ]);


  useEffect(() => {
    dispatch(fetchViasTransporteAsync());
  }, []);

  useEffect(() => {
    if (id) {
      dispatch(fetchByIdAsync(id));
      handleChangeToConsult();
    }
  }, [id]);

  // salva por etapa
  const normalizeData = (data) => {
    const normalizedData = { 
      ...data,
      // Tarifas por peso (aéreo)
      "tarifa45kg":   normalizeNumeral(data.tarifa45kg) || null,
      "tarifa100kg":  normalizeNumeral(data.tarifa100kg) || null,
      "tarifa300kg":  normalizeNumeral(data.tarifa300kg) || null,
      "tarifa500kg":  normalizeNumeral(data.tarifa500kg) || null,
      "tarifa1000kg": normalizeNumeral(data.tarifa1000kg) || null,
      "dry20":  normalizeNumeral(data.dry20) || null,
      "dry40":  normalizeNumeral(data.dry40) || null,
      "graneleiro20": normalizeNumeral(data.graneleiro20) || null,
      "flatRack20": normalizeNumeral(data.flatRack20) || null,
      "flatRack40": normalizeNumeral(data.flatRack40) || null,
      "tank": normalizeNumeral(data.tank) || null,
      "openTop20": normalizeNumeral(data.openTop20) || null,
      "openTop40": normalizeNumeral(data.openTop40) || null,
      "reefer20": normalizeNumeral(data.reefer20) || null,
      "reefer40": normalizeNumeral(data.reefer40) || null,
      "nor20": normalizeNumeral(data.nor20) || null,
      "nor40": normalizeNumeral(data.nor40) || null,
      "plataforma20": normalizeNumeral(data.plataforma20) || null,
      "plataforma40": normalizeNumeral(data.plataforma40) || null,
      "custosAdicionais": data.custosAdicionais?.map((custo) => ({
        ...custo,
        id: validate(custo.id) ? null : custo.id,
        valorCompra: normalizeNumeral(custo.valorCompra) || null,
      })) || [],
    };
  
    return normalizedData;
  }

  const createByStep = async (data, step, next) => {
    console.log("Creating by step:", data, step, next);
    const tenant = localStorage.getItem('tenant');
    const isFirstStep = step === 0;
    const isLastStep = step === 2;

    const executeDebounced = debounce(async () => {
      try {
        data.tenantId = tenant;
        data.viaTransporte = viaTransporteMaritima; // VINCULA AO MARITIMO
        const normalizedData = normalizeData(data);
        if (isFirstStep && !isCreated) {
          const response = await tarifarioAPI.register(normalizedData);

          if (response?.status === 201) {
            next();
            dispatch(resetModel());
            dispatch(success());

            const created = response?.data;

            handleDispatchSetModel(created);
            // dispatch(addToList({ data: created }));

            setIsCreated(true);
          }
        }
      } catch (error) {
        dispatch(failure());
        dispatch(
          setError({
            message: t('com.muralis.qcx.erro.erroSalvarCatalogoProdutosMensagem', {
              erro: error?.response?.data?.message,
            }),
          })
        );
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };

  const update = async (data) => {
    const executeDebounced = debounce(async () => {
      try {
        console.log("Updating data:", data);
        const normalizedData = normalizeData(data);
        const response = await tarifarioAPI.save(normalizedData);

        if (response?.status === 200) {
          const handleResultWithDebounce = debounce(() => {
            handleChangeToConsult();
            dispatch(success());

            const saved = response?.data;

            dispatch(
              setResponse({
                status: response.status,
                data: saved,
                message: t('Operação Realizada com sucesso !!', { frete: saved?.descricaoResumida }),
              })
            );

            dispatch(setModel(saved));
            dispatch(fetchByIdAsync(saved?.id));
          }, 500);

          handleResultWithDebounce();
        }
      } catch ({ response }) {
        dispatch(failure());
        dispatch(
          setError({
            message: t('com.muralis.qcx.erro.erroSalvarCatalogoProdutosEspecifico', {
              catalogo: data?.descricaoResumida,
              erro: response?.data?.message,
            }),
          })
        );
      }
    }, 500);

    dispatch(loading());
    executeDebounced();
  };


  // funcoes de acao

  const handleChangeToCreate = () => {
    dispatch(changeToCreateMode());
  };

  const handleChangeToBackgroundCreate = () => {
    dispatch(changeToBackgroundCreateMode());
  };

  const handleResetBackgroundMode = () => {
    dispatch(resetBackgroundMode());
  };

  const handleChangeToPreparingAction = () => {
    dispatch(preparingAction());
  };

  const handleChangeToConsult = () => {
    dispatch(changeToConsultMode());
    dispatch(resetSubMode());
  };

  const handleChangeToUpdate = () => {
    dispatch(changeToUpdateMode());
  };

  const handleCancelUpdate = () => {
    const currentId = isBackgroundCreate ? freteTarifario?.id : id;

    if (currentId) {
      dispatch(fetchByIdAsync(currentId));
    }

    handleChangeToConsult();
  };

  const handleDispatchSetModel = useCallback(
    (data) => {
      console.log("Dispatching setModel with data:", data);
      dispatch(setModel(data));
    },
    []
  );

  const handleSubmit = async (data, step, next) => {

    delete data.atributosAdicionais;
    const normalizedData = {...data}


    if (step === 2) {
      // step de unidades compartilhadas
      normalizedData.unidadesDeNegocio = unidadesNegocioSelecionadas;

    }

    if (isUpdate) {
      await update(normalizedData, step);
      handleChangeToConsult();
    } else {
      await createByStep(normalizedData, step, next);
    }
  };

  const handleAlternativeSave = async (event, step, next) => {
    console.log("Alternative save at step:", step);
  };


  const breadcrumbs = useMemo(
    () => [
      {
        link: {
          to: '/',
          name: t('com.muralis.qcx.inicio'),
        },
      },
      {
        link: {
          to: t('com.muralis.qcx.url.moduloImportacao'),
          name: t('com.muralis.qcx.importacao.label'),
        },
      },
    ],
    []
  );

  const breadcrumbsProps = {
    maxItems: 3,
  };

  const pageTitle = "Tarifario Marítimo";


  return (
    <QCXRegistrationFormPageTemplate
      pageTitle={pageTitle}
      breadcrumbs={breadcrumbs}
      breadcrumbsProps={breadcrumbsProps}
      isCreate={isCreate} 
      isConsult={isConsult}
      isUpdate={isUpdate}
      isBackgroundCreate={isBackgroundCreate}
      isPreparingAction={isPreparingAction}
      handleAlternativeSave={handleAlternativeSave}
      handleChangeToPreparingAction={handleChangeToPreparingAction}
      handleChangeToCreate={handleChangeToCreate}
      handleChangeToBackgroundCreate={handleChangeToBackgroundCreate}
      handleChangeToConsult={handleChangeToConsult}
      handleChangeToUpdate={handleChangeToUpdate}
      handleCancelUpdate={handleCancelUpdate}
      handleResetBackgroundMode={handleResetBackgroundMode}
      showSubtitle={false}
      authInfo={authInfo}
    >
      {(formProps) => (
        <TarifarioMaritimoWizardFinalForm
          isCreate={isCreate}
          isConsult={isConsult}
          isUpdate={isUpdate}
          isBackgroundCreate={isBackgroundCreate}
          model={freteTarifario}
          handleChangeModel={handleDispatchSetModel}
          handleSubmit={handleSubmit}
          authInfo={authInfo}
          requiredRoles={['armazem']}
          {...formProps}
        />
      )}
    </QCXRegistrationFormPageTemplate>
  );
}